import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Restore as RestoreIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useProjectWorkflow } from '../../contexts/ProjectWorkflowContext';
import {
  deletionRestoreService,
  RestorableRecord,
} from '../../services/deletionRestoreService';
import { completeArchiveRestore } from '../../utils/workflowPanelCache';

type RestoreCenterPageProps = {
  /** Super Admin settings page vs client/agency self-service archive */
  variant?: 'admin' | 'self';
};

const RestoreCenterPage: React.FC<RestoreCenterPageProps> = ({ variant = 'admin' }) => {
  const isSelfService = variant === 'self';
  const { isClient, isAgency, isAdmin } = useAuth();
  const { bumpProjectsRefresh, bumpClientsRefresh } = useProjectWorkflow();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [entityType, setEntityType] = useState('');
  const [search, setSearch] = useState('');
  const [selectedItems, setSelectedItems] = useState<Map<string, RestorableRecord>>(new Map());
  const [confirmRecord, setConfirmRecord] = useState<RestorableRecord | null>(null);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const { data: settingsData, isLoading: settingsLoading } = useQuery({
    queryKey: ['deletion-restore-settings'],
    queryFn: () => deletionRestoreService.getSettings(),
    enabled: !isSelfService,
  });

  const { data: entityTypesData } = useQuery({
    queryKey: ['deletion-restore-entity-types'],
    queryFn: () => deletionRestoreService.getEntityTypes(),
  });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['deletion-restore-records', page, rowsPerPage, entityType, search],
    queryFn: () =>
      deletionRestoreService.listRecords({
        page: page + 1,
        limit: rowsPerPage,
        entityType: entityType || undefined,
        search: search || undefined,
      }),
  });

  const settings = isSelfService ? data?.settings : settingsData?.data;
  const records = useMemo(() => data?.data ?? [], [data?.data]);
  const total = data?.pagination?.total || 0;
  const entityTypes = entityTypesData?.data || [];

  const pageIds = useMemo(() => records.map((row) => row.id), [records]);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedItems.has(id));
  const somePageSelected = pageIds.some((id) => selectedItems.has(id)) && !allPageSelected;

  const selectedRecords = useMemo(
    () => Array.from(selectedItems.values()),
    [selectedItems],
  );

  const selectedRelatedCount = useMemo(
    () => selectedRecords.reduce((sum, row) => sum + (row.relatedCount || 0), 0),
    [selectedRecords],
  );

  useEffect(() => {
    setSelectedItems(new Map());
  }, [entityType, search]);

  const toggleRow = (row: RestorableRecord) => {
    setSelectedItems((prev) => {
      const next = new Map(prev);
      if (next.has(row.id)) next.delete(row.id);
      else next.set(row.id, row);
      return next;
    });
  };

  const toggleAllOnPage = () => {
    setSelectedItems((prev) => {
      const next = new Map(prev);
      if (allPageSelected) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        records.forEach((row) => next.set(row.id, row));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedItems(new Map());

  const updateSettingsMutation = useMutation({
    mutationFn: (payload: { enabled?: boolean; retentionDays?: number }) =>
      deletionRestoreService.updateSettings(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deletion-restore-settings'] });
      setSnackbar({ open: true, message: 'Restore settings updated', severity: 'success' });
    },
    onError: (err: any) => {
      setSnackbar({
        open: true,
        message: err?.response?.data?.error || 'Failed to update settings',
        severity: 'error',
      });
    },
  });

  const refreshWorkflowAfterRestore = () => {
    void completeArchiveRestore(queryClient);
    bumpProjectsRefresh();
    bumpClientsRefresh();
  };

  const restoreMutation = useMutation({
    mutationFn: (id: string) => deletionRestoreService.restore(id),
    onSuccess: (result) => {
      setConfirmRecord(null);
      clearSelection();
      refetch();
      refreshWorkflowAfterRestore();
      setSnackbar({ open: true, message: result.message || 'Restored successfully', severity: 'success' });
    },
    onError: (err: any) => {
      setSnackbar({
        open: true,
        message: err?.response?.data?.error || 'Failed to restore item',
        severity: 'error',
      });
    },
  });

  const bulkRestoreMutation = useMutation({
    mutationFn: (ids: string[]) => deletionRestoreService.restoreBulk(ids),
    onSuccess: (result) => {
      setBulkConfirmOpen(false);
      clearSelection();
      refetch();
      refreshWorkflowAfterRestore();
      const failedCount = result.data?.failed?.length || 0;
      setSnackbar({
        open: true,
        message: result.message || `${result.data?.restored || 0} item(s) restored`,
        severity: failedCount > 0 ? 'error' : 'success',
      });
    },
    onError: (err: any) => {
      setSnackbar({
        open: true,
        message: err?.response?.data?.error || 'Failed to restore selected items',
        severity: 'error',
      });
    },
  });

  const retentionInput = useMemo(() => String(settings?.retentionDays ?? 7), [settings?.retentionDays]);

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate(isSelfService ? '/dashboard' : '/settings')} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#1e293b' }}>
            {isSelfService ? 'My Archive' : 'Restore Archived Data'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            {isSelfService
              ? isClient
                ? 'View and restore your own archived projects within the retention window.'
                : isAgency
                  ? 'View and restore your own archived clients and projects within the retention window.'
                  : isAdmin
                    ? 'View and restore archived clients, projects, and placements for your assigned clients, plus sites and publishers you archived.'
                    : 'View and restore your own archived items within the retention window.'
              : 'Super Admin can restore archived clients, projects, publishers, sites, and placements within the retention window.'}
          </Typography>
        </Box>
      </Box>

      {!isSelfService && (
      <Paper sx={{ p: 2.5, mb: 3, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <SettingsIcon sx={{ color: '#64748b' }} />
          <Typography sx={{ fontWeight: 600 }}>Restore settings</Typography>
        </Box>

        {settingsLoading ? (
          <CircularProgress size={24} />
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings?.enabled ?? true}
                  onChange={(e) => updateSettingsMutation.mutate({ enabled: e.target.checked })}
                  disabled={updateSettingsMutation.isPending}
                />
              }
              label="Keep archive snapshots for restore"
            />
            <TextField
              label="Retention (days)"
              type="number"
              size="small"
              defaultValue={retentionInput}
              key={retentionInput}
              inputProps={{ min: 1, max: 30 }}
              sx={{ width: 160 }}
              onBlur={(e) => {
                const value = parseInt(e.target.value, 10);
                if (!Number.isNaN(value) && value >= 1 && value <= 30 && value !== settings?.retentionDays) {
                  updateSettingsMutation.mutate({ retentionDays: value });
                }
              }}
            />
            <Typography variant="body2" color="text.secondary">
              Deleted items older than this are permanently purged from restore archive.
            </Typography>
          </Box>
        )}
      </Paper>
      )}

      {!isSelfService && !settings?.enabled && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Restore archiving is currently disabled. New archives will not appear here until you turn it back on.
        </Alert>
      )}

      {isSelfService && settings?.enabled === false && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Archive restore is temporarily unavailable. Contact your administrator if you need an item restored.
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            label="Search label"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 220 }}
          />
          <TextField
            select
            size="small"
            label="Entity type"
            value={entityType}
            onChange={(e) => {
              setEntityType(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">All types</MenuItem>
            {entityTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {deletionRestoreService.formatEntityType(type)}
              </MenuItem>
            ))}
          </TextField>
          {selectedItems.size > 0 && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                {selectedItems.size} selected
              </Typography>
              <Button size="small" onClick={clearSelection}>
                Clear
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<RestoreIcon />}
                onClick={() => setBulkConfirmOpen(true)}
              >
                Restore selected
              </Button>
            </>
          )}
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load deleted records. Make sure the database migration has been applied.
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allPageSelected}
                  indeterminate={somePageSelected}
                  onChange={toggleAllOnPage}
                  disabled={records.length === 0}
                  inputProps={{ 'aria-label': 'Select all on page' }}
                />
              </TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Label</TableCell>
              <TableCell>Deleted by</TableCell>
              <TableCell>Deleted at</TableCell>
              <TableCell>Days left</TableCell>
              <TableCell>Related</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : records.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#64748b' }}>
                  No restorable deleted records in the last {settings?.retentionDays ?? 7} days.
                </TableCell>
              </TableRow>
            ) : (
              records.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  selected={selectedItems.has(row.id)}
                  onClick={() => toggleRow(row)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      size="small"
                      checked={selectedItems.has(row.id)}
                      onChange={() => toggleRow(row)}
                      inputProps={{ 'aria-label': `Select ${row.label}` }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={deletionRestoreService.formatEntityType(row.entityType)} />
                  </TableCell>
                  <TableCell>{row.label}</TableCell>
                  <TableCell>{deletionRestoreService.formatUserName(row.deletedBy)}</TableCell>
                  <TableCell>{new Date(row.deletedAt).toLocaleString()}</TableCell>
                  <TableCell>{deletionRestoreService.daysRemaining(row.expiresAt)}</TableCell>
                  <TableCell>{row.relatedCount || 0}</TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<RestoreIcon />}
                      onClick={() => setConfirmRecord(row)}
                    >
                      Restore
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </TableContainer>

      <Dialog open={!!confirmRecord} onClose={() => setConfirmRecord(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm restore (re-publish)</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            Restore <strong>{confirmRecord?.label}</strong> ({deletionRestoreService.formatEntityType(confirmRecord?.entityType || '')})?
            This re-activates the archived record and shows it in lists again.
          </Typography>
          {!!confirmRecord?.relatedCount && (
            <Typography variant="body2" color="text.secondary">
              This will also restore {confirmRecord.relatedCount} related record(s) captured at archive time.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRecord(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => confirmRecord && restoreMutation.mutate(confirmRecord.id)}
            disabled={restoreMutation.isPending}
          >
            {restoreMutation.isPending ? 'Restoring…' : 'Restore'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkConfirmOpen} onClose={() => setBulkConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Restore {selectedItems.size} selected item(s)</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            Restore <strong>{selectedItems.size}</strong> archived record(s)? They will re-appear in their modules.
          </Typography>
          {selectedRelatedCount > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This may also restore up to {selectedRelatedCount} related record(s) captured at archive time.
            </Typography>
          )}
          <Box sx={{ maxHeight: 220, overflowY: 'auto' }}>
            {selectedRecords.map((row) => (
              <Typography key={row.id} variant="body2" sx={{ py: 0.25 }}>
                • {row.label} ({deletionRestoreService.formatEntityType(row.entityType)})
              </Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => bulkRestoreMutation.mutate(Array.from(selectedItems.keys()))}
            disabled={bulkRestoreMutation.isPending || selectedItems.size === 0}
          >
            {bulkRestoreMutation.isPending ? 'Restoring…' : `Restore ${selectedItems.size}`}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
      />
    </Box>
  );
};

export default RestoreCenterPage;
